import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function ClosingAMonth() {
  return (
    <>
      <p>
        Each customer has one month (billing period) per UTC calendar month. It is <strong>open</strong> while usage can
        still change it, and its invoice is a live preview. <strong>Closing</strong> it fixes the invoice for good.{" "}
        <ScreenLink href="/invoices">Invoices</ScreenLink> lists every customer's months; a month that has ended but is
        still open shows as due. Billing admins close months.
      </p>

      <h2>What closing does</h2>
      <ul>
        <li>Prices the month's usage, exactly as the reports do, and writes the invoice lines. They never change again.</li>
        <li>
          For a <strong>billed</strong> customer, it also writes the customer invoice from the month's rate plan, trials,
          approved adjustments and carried credit, in the same step.
        </li>
        <li>For a not-billed customer, it closes the usage record only.</li>
        <li>
          Closing twice is harmless: the second time shows the same invoice.
        </li>
      </ul>

      <h2 id="before-you-close">Before you close</h2>
      <ol>
        <li>The month must be over (UTC). If some of its usage is still being rolled up, the close is refused; try again once the worker has caught up.</li>
        <li>
          Check <ScreenLink href="/reconciliation">Reconciliation</ScreenLink>: missing events and Bedrock drift are easier
          to explain before the invoice goes out.
        </li>
        <li>
          On the customer's Billing tab, check the plan for the month, and approve or void any draft adjustments.
        </li>
        <li>Read the invoice preview. It is exactly what the close will fix.</li>
        <li>
          Close billed months in order: a month cannot close while an earlier one of the same customer is still open, or
          while a billed month has no rate plan.
        </li>
      </ol>

      <h2 id="forcing">Forcing a close</h2>
      <p>A close is refused when the month's usage would be priced at:</p>
      <ul>
        <li>
          an <strong>unverified</strong> model price, or
        </li>
        <li>
          <strong>no price</strong> at all, for a model with usage.
        </li>
      </ul>
      <p>
        Fix the price first if you can (<HelpLink to="model-prices" section="adding-a-price" />). If finance accepts the
        gap, <strong>force</strong> the close with a reason of 10 to 500 characters. The reason is kept on the month and in
        the audit log. Force waives only those two checks, never “month not over” or “usage still being rolled up”.
      </p>

      <h2 id="invoices-and-statements">Invoices and statements</h2>
      <table>
        <thead>
          <tr>
            <th>Document</th>
            <th>Who reads it</th>
            <th>Shows</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Invoice</td>
            <td>AllCode billing staff</td>
            <td>Every line with its AWS cost, price, profit and the rule that priced it. Downloadable as CSV.</td>
          </tr>
          <tr>
            <td>Statement</td>
            <td>The customer</td>
            <td>The lines and the total only: no AWS cost, profit, reasons or ids.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Open a month from Invoices or from the customer's Billing tab to see both. Late usage that arrives after a month
        closed (up to 14 days after the close) goes on the customer's <strong>next</strong> invoice as late lines, priced
        on the earlier month's terms.
      </p>
      <Callout>
        A customer on the actual-AWS-bill cost basis gets its invoice later: the close fixes the usage, and the invoice is
        written when that month's AWS cost is finalized. See <HelpLink to="aws-costs" section="finalizing" />.
      </Callout>

      <Related to={["pricing-and-markup", "usage-adjustments", "reconciliation", "aws-costs"]} />
    </>
  );
}
